import { ref, type Ref } from "vue"
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  doc,
  type Unsubscribe,
  type Timestamp,
  type Firestore,
} from "firebase/firestore"
import { useFirestore } from "vuefire" // Keep this, might be needed if setDbInstance is not used from outside
import type {
  Ticket,
  CustomerSnapshot,
  TicketStatus,
  TicketPriority,
  TicketSource,
  MessagePreview,
  OrderSnapshot,
  TicketMessageUnion,
  BaseTicketMessage,
  PersonSnapshot,
  TicketTextMessage,
  TicketFileMessage,
  TicketImageMessage,
  TicketInternalNoteMessage,
} from "@wowpress/types"
import { useSetting } from "@/composables/useSetting"

// --- 전역 상태 정의 ---
export const ticketLoading = ref(false)
export const ticketError = ref<Error | null>(null)
export const messageLoading = ref(false)
export const messageError = ref<Error | null>(null)
export const ticketMessages: Ref<TicketMessageUnion[]> = ref([])
export const recentTicket: Ref<Ticket | null> = ref(null)

const { chatbot } = useSetting()

// --- 내부 상태 (구독 해제 함수) ---
let ticketUnsubscribe: Unsubscribe | null = null
let messagesUnsubscribe: Unsubscribe | null = null
let currentDbInstance: Firestore | null = null // Firestore 인스턴스 저장

// --- 구독 관리 함수 ---

const unsubscribeFromTicket = () => {
  if (ticketUnsubscribe) {
    console.log("[SubscriptionManager] Unsubscribing from recent ticket listener.")
    ticketUnsubscribe()
    ticketUnsubscribe = null
  }
  // recentTicket.value = null; // 여기서 즉시 null로 만들지 않음
}

const unsubscribeFromMessages = () => {
  if (messagesUnsubscribe) {
    console.log("[SubscriptionManager] Unsubscribing from messages.")
    messagesUnsubscribe()
    messagesUnsubscribe = null
  }
  ticketMessages.value = [] // 상태 초기화
}

const subscribeToRecentTicketInternal = (userId: string) => {
  unsubscribeFromTicket() // 리스너만 해제

  if (!userId || !currentDbInstance) {
    console.log("[SubscriptionManager] No user ID or DB instance for ticket subscription.")
    recentTicket.value = null // 여기서 null 설정
    ticketLoading.value = false
    unsubscribeFromMessages() // DB 없으면 메시지 구독도 확실히 해제
    return
  }

  console.log(`[SubscriptionManager] Subscribing to recent ticket for user: ${userId}`)
  ticketLoading.value = true
  ticketError.value = null

  try {
    const ticketsCollectionRef = collection(currentDbInstance, "tickets")
    const q = query(ticketsCollectionRef, where("customer.id", "==", userId), orderBy("updatedAt", "desc"), limit(1))

    ticketUnsubscribe = onSnapshot(
      q,
      (snapshot) => {
        console.log("[SubscriptionManager] Ticket Snapshot received.")
        if (!snapshot.empty) {
          const docSnapshot = snapshot.docs[0]
          const data = docSnapshot.data()
          console.log("[SubscriptionManager] Found document:", docSnapshot.id, data)

          if (data.customer && data.title && data.description && data.status && data.priority) {
            const ticketData: Ticket = {
              id: docSnapshot.id,
              customer: data.customer as CustomerSnapshot,
              title: data.title as string,
              description: data.description as string,
              status: data.status as TicketStatus,
              priority: data.priority as TicketPriority,
              source: data.source as TicketSource | undefined,
              tags: data.tags as string[] | null | undefined,
              assignedTo: data.assignedTo as string | null | undefined,
              assignedBy: data.assignedBy as string | null | undefined,
              assignedAt: data.assignedAt as Timestamp | undefined,
              references: data.references as string[] | undefined,
              customerFeedbackId: data.customerFeedbackId as string | undefined,
              chatbotSummary: data.chatbotSummary as any,
              chatbotDuration: data.chatbotDuration as number | undefined,
              createdAt: data.createdAt as Timestamp,
              updatedAt: data.updatedAt as Timestamp,
              resolvedAt: data.resolvedAt as Timestamp | undefined,
              closedAt: data.closedAt as Timestamp | undefined,
              lastMessage: data.lastMessage
                ? ({
                    ...data.lastMessage,
                    createdAt: data.lastMessage.createdAt as Timestamp,
                  } as MessagePreview)
                : undefined,
              lastState: data.lastState as string | undefined,
              metadata: data.metadata as Record<string, any> | undefined,
              orderId: data.orderId as string | undefined,
              ...(data.order && { order: data.order as OrderSnapshot }),
            }
            console.log("[SubscriptionManager] Ticket data validated, updating recentTicket:", ticketData.id)
            const previousTicketId = recentTicket.value?.id
            recentTicket.value = ticketData

            if (previousTicketId !== ticketData.id) {
              console.log(
                "[SubscriptionManager] Ticket ID changed or was null, subscribing to messages:",
                ticketData.id,
              )
              subscribeToMessagesInternal(ticketData.id)
            }
          } else {
            console.warn(
              "[SubscriptionManager] Firestore data missing required fields or invalid:",
              docSnapshot.id,
              data,
            )
            if (recentTicket.value?.id !== docSnapshot.id) {
              console.log(
                "[SubscriptionManager] Document ID mismatch or initial load with invalid data. Setting recentTicket to null.",
              )
              recentTicket.value = null
              unsubscribeFromMessages()
            } else {
              console.warn(
                "[SubscriptionManager] Document ID matches existing ticket, but data is incomplete. Retaining current ticket state temporarily.",
              )
            }
          }
        } else {
          console.log("[SubscriptionManager] No recent open/pending ticket found (snapshot empty).")
          recentTicket.value = null // 티켓 없을 시 null 설정
          unsubscribeFromMessages() // 티켓이 없으면 메시지 구독도 해제
        }
        console.log("[SubscriptionManager] Setting ticketLoading to false (success).")
        ticketLoading.value = false
      },
      (error) => {
        console.error("[SubscriptionManager] Ticket Snapshot error:", error)
        ticketError.value = error
        recentTicket.value = null // 에러 시 null 설정
        console.log("[SubscriptionManager] Setting ticketLoading to false (snapshot error).")
        ticketLoading.value = false
        // unsubscribeFromTicket(); // 이미 리스너 에러로 해제됨
        unsubscribeFromMessages()
      },
    )
    console.log("[SubscriptionManager] Ticket onSnapshot listener attached.")
  } catch (e: any) {
    console.error("[SubscriptionManager] Error setting up ticket subscription:", e)
    ticketError.value = e
    recentTicket.value = null // 설정 에러 시 null 설정
    console.log("[SubscriptionManager] Setting ticketLoading to false (setup error).")
    ticketLoading.value = false
    unsubscribeFromMessages() // 에러 시 메시지 구독 해제
  }
}

const subscribeToMessagesInternal = (ticketId: string | null) => {
  unsubscribeFromMessages() // 기존 메시지 구독 해제

  if (!ticketId || !currentDbInstance) {
    console.log("[SubscriptionManager] No ticket ID or DB for message subscription.")
    ticketMessages.value = []
    messageLoading.value = false
    return
  }
  console.log(`[SubscriptionManager] Subscribing to messages for ticket: ${ticketId}`)
  messageLoading.value = true
  messageError.value = null

  try {
    const messagesColRef = collection(currentDbInstance, "tickets", ticketId, "messages")
    const q = query(messagesColRef, orderBy("createdAt", "asc"))

    messagesUnsubscribe = onSnapshot(
      q,
      (snapshot) => {
        console.log(`[SubscriptionManager] Received ${snapshot.docs.length} messages for ticket ${ticketId}`)
        ticketMessages.value = snapshot.docs
          .map((doc) => {
            const data = doc.data()
            if (!data || !data.author || !data.createdAt || !data.ticketId || !data.type) {
              console.warn("[SubscriptionManager] Invalid message data:", doc.id, data)
              return null
            }

            const baseData = {
              id: doc.id,
              author: data.author as PersonSnapshot,
              createdAt: data.createdAt as Timestamp, // Timestamp 유지
              ticketId: data.ticketId as string,
              metadata: data.metadata as Record<string, any> | undefined,
              status: data.status as BaseTicketMessage["status"],
              readBy: data.readBy as string[] | undefined,
            }

            switch (data.type) {
              case "text":
                return { ...baseData, type: "text", text: data.text as string } as TicketTextMessage
              case "file":
                return {
                  ...baseData,
                  type: "file",
                  name: data.name as string,
                  uri: data.uri as string,
                  size: data.size as number,
                  mimeType: data.mimeType as string | undefined,
                } as TicketFileMessage
              case "image":
                return { ...baseData, type: "image", uri: data.uri as string } as TicketImageMessage
              case "internal_note":
                return { ...baseData, type: "internal_note", text: data.text as string } as TicketInternalNoteMessage
              default:
                console.warn(`[SubscriptionManager] Unknown message type: ${data.type}`)
                return null
            }
          })
          .filter((msg): msg is TicketMessageUnion => msg !== null)

        messageLoading.value = false
      },
      (error) => {
        console.error(`[SubscriptionManager] Message subscription error for ticket ${ticketId}:`, error)
        messageError.value = error
        ticketMessages.value = []
        messageLoading.value = false
        unsubscribeFromMessages()
      },
    )
    console.log(`[SubscriptionManager] Message listener attached for ticket ${ticketId}.`)
  } catch (error) {
    console.error(`[SubscriptionManager] Error setting up message subscription for ticket ${ticketId}:`, error)
    messageError.value = new Error("Failed to subscribe to messages.")
    ticketMessages.value = []
    messageLoading.value = false
  }
}

// --- 공개 함수 ---
/**
 * Firestore 구독을 초기화합니다. useAuth.ts 등에서 사용자 로그인/로그아웃 시 호출해야 합니다.
 * @param userId 현재 로그인한 사용자의 ID 또는 null (로그아웃 시)
 */
export const initializeSubscriptions = (userId: string | null) => {
  // Firestore 인스턴스가 설정되었는지 확인
  if (!currentDbInstance) {
    console.error(
      "[SubscriptionManager] Firestore instance is not set. Call setDbInstance() before initializing subscriptions.",
    )
    return // DB 인스턴스 없으면 중단
  }

  if (userId) {
    console.log(`[SubscriptionManager] Initializing subscriptions for user: ${userId}`)
    // 티켓 구독 시작 (내부적으로 메시지 구독도 트리거됨)
    subscribeToRecentTicketInternal(userId)
  } else {
    console.log("[SubscriptionManager] User logged out, cleaning up subscriptions.")
    cleanupSubscriptions()
  }
}

/**
 * 모든 활성 Firestore 구독을 해제하고 상태를 초기화합니다.
 */
export const cleanupSubscriptions = () => {
  console.log("[SubscriptionManager] Cleaning up all subscriptions.")
  unsubscribeFromTicket()
  unsubscribeFromMessages()
  // 상태 초기화 (여기서 null 설정 확실히)
  recentTicket.value = null
  ticketLoading.value = false
  ticketError.value = null
  messageLoading.value = false
  messageError.value = null
}

/**
 * SubscriptionManager에서 사용할 Firestore 인스턴스를 설정합니다.
 * Vue 앱 초기화 시 또는 useAuth 컴포저블 내에서 호출하는 것이 좋습니다.
 * @param db 사용할 Firestore 인스턴스
 */
export const setDbInstance = (db: Firestore) => {
  console.log("[SubscriptionManager] Setting Firestore instance.")
  currentDbInstance = db
}
